import { makeBlock } from './types.js';
import { subtreeIds, findParent, isAncestor } from './normalize.js';
import { getBlockDefinition } from './registry.js';

/**
 * Insert a new block into the doc.
 *
 * @param {object} doc
 * @param {string|null} parentId  – null means root
 * @param {number} index          – insertion index in parent's child list
 * @param {string} type           – block type
 * @param {object} [initialAttrs] – merged with definition defaults
 * @returns {object} new doc
 */
export function insertBlock(doc, parentId, index, type, initialAttrs = {}) {
  const def = getBlockDefinition(type);
  const defaults = def && def.schema && typeof def.schema.defaults === 'function'
    ? def.schema.defaults()
    : {};
  const isContainer = !!(def && def.capabilities && def.capabilities.isContainer);
  const block = makeBlock(type, { ...defaults, ...initialAttrs }, isContainer ? [] : undefined);

  const blocks = { ...doc.blocks, [block.id]: block };

  if (parentId === null) {
    const root = [...doc.root];
    root.splice(index, 0, block.id);
    return { root, blocks };
  }

  const parent = blocks[parentId];
  if (!parent || !Array.isArray(parent.children)) return doc; // invalid
  const children = [...parent.children];
  children.splice(index, 0, block.id);
  blocks[parentId] = { ...parent, children };
  return { ...doc, blocks };
}

/**
 * Remove a block and its entire subtree from the doc.
 *
 * @param {object} doc
 * @param {string} id
 * @returns {object} new doc
 */
export function removeBlock(doc, id) {
  const location = findParent(doc, id);
  if (!location) return doc;

  const idsToRemove = new Set(subtreeIds(doc, id));

  // Remove from parent list
  let root = doc.root;
  const blocks = { ...doc.blocks };

  if (location.parentId === null) {
    root = root.filter((bid) => bid !== id);
  } else {
    const parent = blocks[location.parentId];
    blocks[location.parentId] = {
      ...parent,
      children: parent.children.filter((bid) => bid !== id),
    };
  }

  // Remove all subtree blocks
  for (const rid of idsToRemove) {
    delete blocks[rid];
  }

  return { root, blocks };
}

/**
 * Move a block to a new parent/index.
 *
 * @param {object} doc
 * @param {string} id
 * @param {string|null} toParentId
 * @param {number} toIndex
 * @returns {object} new doc
 */
export function moveBlock(doc, id, toParentId, toIndex) {
  // Cannot move a block into one of its own descendants
  if (toParentId !== null && isAncestor(doc, id, toParentId)) return doc;
  if (toParentId === id) return doc;

  const from = findParent(doc, id);
  if (!from) return doc;

  // Perform removal first
  let next = removeBlock(doc, id);

  // No index adjustment needed: after removing the block from its original
  // position, the target index is already correct for the post-removal array.
  const adjustedIndex = toIndex;

  const block = doc.blocks[id];
  if (!block) return next;

  // Re-add the block to the new blocks map
  next = { ...next, blocks: { ...next.blocks, [id]: block } };

  if (toParentId === null) {
    const root = [...next.root];
    root.splice(adjustedIndex, 0, id);
    return { ...next, root };
  }

  const parent = next.blocks[toParentId];
  if (!parent || !Array.isArray(parent.children)) return next;
  const children = [...parent.children];
  children.splice(adjustedIndex, 0, id);
  next = {
    ...next,
    blocks: { ...next.blocks, [toParentId]: { ...parent, children } },
  };
  return next;
}

/**
 * Update the attrs of a block (shallow merge patch).
 *
 * @param {object} doc
 * @param {string} id
 * @param {object} patch
 * @returns {object} new doc
 */
export function updateAttrs(doc, id, patch) {
  const block = doc.blocks[id];
  if (!block) return doc;
  return {
    ...doc,
    blocks: {
      ...doc.blocks,
      [id]: { ...block, attrs: { ...block.attrs, ...patch } },
    },
  };
}

/**
 * Duplicate a block and its subtree, inserting the copy immediately after the
 * original.
 *
 * @param {object} doc
 * @param {string} id
 * @returns {{ doc: object, newId: string }}
 */
export function duplicateBlock(doc, id) {
  const location = findParent(doc, id);
  if (!location) return { doc, newId: null };

  const idMap = {};
  const blocks = { ...doc.blocks };

  const cloneBlock = (srcId) => {
    const src = doc.blocks[srcId];
    if (!src) return null;
    const newId = makeBlock(src.type, {}).id;
    idMap[srcId] = newId;
    const newBlock = {
      ...src,
      id: newId,
      attrs: { ...src.attrs },
    };
    if (Array.isArray(src.children)) {
      newBlock.children = src.children.map(cloneBlock).filter(Boolean);
    }
    blocks[newId] = newBlock;
    return newId;
  };

  const newRootId = cloneBlock(id);
  if (!newRootId) return { doc, newId: null };

  let newDoc = { ...doc, blocks };

  const insertAfter = location.index + 1;
  if (location.parentId === null) {
    const root = [...newDoc.root];
    root.splice(insertAfter, 0, newRootId);
    newDoc = { ...newDoc, root };
  } else {
    const parent = newDoc.blocks[location.parentId];
    const children = [...parent.children];
    children.splice(insertAfter, 0, newRootId);
    newDoc = {
      ...newDoc,
      blocks: {
        ...newDoc.blocks,
        [location.parentId]: { ...parent, children },
      },
    };
  }

  return { doc: newDoc, newId: newRootId };
}
